/**
 * Modül hakları sözleşmesi (v4.5 A-0; belge: docs/planning/ekosistem/moduler-urun-portal-ve-teslim.md § 0, § 3.3, § 4, § 14).
 *
 * Plan kapısının UZANTISIDIR, ikinci bir hak sistemi değildir: modül kararı da `PLAN_LIMIT` (403) ile reddedilir
 * (`reason: 'module_off' | 'module_read_only'`). Bugünkü 5 plan özelliği ve 13 tavan modüle taşınmadı; onlar `plans.ts`'te kalır.
 *
 * - Modül anahtarları yalnız YENİ yeteneklere aittir. Bugün bir kiracının kullanabildiği hiçbir işlev (ajan çekirdeği dahil) modül
 *   koruyucusu almaz (§ 0 A).
 * - Etkin hak = planın dahil ettiği modül (`plan_modules`; bu tur hiçbir plan modül içermez, ticari karar K-6) YA DA kiracıya açıkça
 *   verilmiş hak (`tenant_modules`, yalnız operatör yazar). Satır yoksa `off`.
 * - Kapanan modül `read_only` olur: veri silinmez, iç kullanıcı geçmişi okur ve dışa aktarır, yeni işlem reddedilir.
 * - Bağımlılık: bir modülün etkin durumu bağımlı olduğu modülün etkin durumunu aşamaz (portal, teslim modülü kapalıyken veri açmaz).
 * - Modül hakkı kiracıya, kayıt/işlem yetkisi kullanıcıya uygulanır; ikisi birlikte aranır (modül hakkı ACL'yi genişletmez).
 */
import { z } from 'zod';
export const PLAN_MODULES = ['delivery', 'portal'];
export const PLAN_MODULE_LABELS = {
    delivery: 'Proje ve hizmet teslimi',
    portal: 'Müşteri portalı',
};
/** Açık bağımlılıklar: anahtar → etkin olması gereken modüller */
export const PLAN_MODULE_DEPENDS = {
    delivery: [],
    portal: ['delivery'],
};
/** Etkin durum: `off` (hak yok) < `read_only` (kapandı; geçmiş okunur) < `active` */
export const MODULE_STATES = ['off', 'read_only', 'active'];
/** Kiracı hakkı satırında saklanabilen durumlar (`off` satır yokluğudur) */
export const MODULE_GRANT_STATES = ['active', 'read_only'];
export const MODULE_SOURCES = ['none', 'plan', 'grant'];
const RANK = { off: 0, read_only: 1, active: 2 };
export function minModuleState(a, b) {
    return RANK[a] <= RANK[b] ? a : b;
}
export const moduleDecisionSchema = z.object({
    state: z.enum(MODULE_STATES),
    source: z.enum(MODULE_SOURCES),
    /** Süreli hak bitişi (ISO); yoksa `null` */
    endsAt: z.string().nullable(),
    /** Bağımlılık nedeniyle düşürüldüyse bağımlı olunan modül */
    limitedBy: z.enum(PLAN_MODULES).nullable(),
});
export const moduleDecisionsSchema = z.object(Object.fromEntries(PLAN_MODULES.map((m) => [m, moduleDecisionSchema])));
/**
 * Etkin modül kararları (saf; sunucu ve arayüz aynı kuralı kullanır). Sıra: kiracı hakkı satırı varsa o (süresi geçmiş `active`
 * → `read_only`), yoksa plan dahil ediyorsa `active`, değilse `off`; sonra bağımlılıklar uygulanır.
 */
export function resolveModuleDecisions(input) {
    const base = new Map();
    for (const m of PLAN_MODULES) {
        const g = input.grants.find((x) => x.module === m);
        if (g !== undefined) {
            const expired = g.endsAt !== null && g.endsAt.getTime() <= input.now.getTime();
            base.set(m, {
                state: g.state === 'active' && expired ? 'read_only' : g.state,
                source: 'grant',
                endsAt: g.endsAt,
            });
        }
        else if (input.planModules.includes(m)) {
            base.set(m, { state: 'active', source: 'plan', endsAt: null });
        }
        else {
            base.set(m, { state: 'off', source: 'none', endsAt: null });
        }
    }
    const out = {};
    const resolve = (m, seen) => {
        const done = out[m];
        if (done !== undefined)
            return done;
        if (seen.includes(m))
            throw new Error(`modül bağımlılığı döngüsel: ${[...seen, m].join(' → ')}`);
        const b = base.get(m);
        if (b === undefined)
            throw new Error(`bilinmeyen modül: ${m}`);
        let state = b.state;
        let limitedBy = null;
        for (const d of PLAN_MODULE_DEPENDS[m]) {
            const dep = resolve(d, [...seen, m]);
            const next = minModuleState(state, dep.state);
            if (next !== state) {
                state = next;
                limitedBy = d;
            }
        }
        const decision = {
            state,
            source: b.source,
            endsAt: b.endsAt === null ? null : b.endsAt.toISOString(),
            limitedBy,
        };
        out[m] = decision;
        return decision;
    };
    for (const m of PLAN_MODULES)
        resolve(m, []);
    return out;
}
export function moduleAllows(state, access) {
    return access === 'read' ? state !== 'off' : state === 'active';
}
/** `PLAN_LIMIT` ayrıntısı (modül): `plans.ts`'teki `planLimitDetailsSchema`'nın modül kolu */
export const moduleLimitDetailsSchema = z.object({
    reason: z.enum(['module_off', 'module_read_only']),
    module: z.enum(PLAN_MODULES),
    state: z.enum(MODULE_STATES),
    limitedBy: z.enum(PLAN_MODULES).nullable(),
});
/** Operatör modül değişikliği (CLI / konsol): gerekçe zorunlu, denetim kaydına yazılır */
export const MODULE_CHANGE_ACTIONS = ['grant', 'close', 'reopen', 'remove'];
//# sourceMappingURL=modules.js.map